import { DashboardContent } from 'src/layouts/dashboard';
import { useTranslate } from 'react-admin';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import ErrorPage403 from 'src/pages/error/403';
import { Grid, Box, Button, Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Paper, InputAdornment, Chip, Divider} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import useEarlyBird from './useEarlyBird';
import TablePagination from '@mui/material/TablePagination';
import { downloadEarlyBirdCSV } from 'src/pages/admin/services/whitelistService';

export interface EarlyBirdRecord {
  userId: string;
  rank: number | null;
  walletAddress: string;
  email: string;
  cumulativeVolumeUsdc: string;
  reward: number;
  claimed: boolean;
  proxyWalletAddress: string;
  rewardUsdt: string;
  claimSlot: any
  claimedAt: any
  transferStatus: any
}

export interface EarlyBirdSummaryTier {
  amountUsdc: string;
  countTotal: number;
  countPaid: number;
  subtotalCommittedUsdc: string;
  subtotalPaidUsdc: string;
}

export interface EarlyBirdSummary {
  totalQuota: number;
  received: number;
  remaining: number;
  rewardsIssuedUsdt: string;
  tiers: EarlyBirdSummaryTier[];
}

const View = () => {
  const translate = useTranslate();

  const [searchTerm, setSearchTerm] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { retrieve, retrieveSummary, summary, records, total } = useEarlyBird();

  useEffect(() => {
    retrieve(page + 1, rowsPerPage);
    retrieveSummary();
  }, [page, rowsPerPage, retrieve, retrieveSummary]);

  const filteredRecords = useMemo(() => {
    if (!searchTerm.trim()) return records;
    const lowerTerm = searchTerm.toLowerCase();
    return records.filter(
      (r) =>
        r.proxyWalletAddress.toLowerCase().includes(lowerTerm) ||
        r.email.toLowerCase().includes(lowerTerm)
    );
  }, [records, searchTerm]);

  const exportToCSV = async () => {
    try {
      const response = await downloadEarlyBirdCSV();

      const blob = new Blob([response.data], {
        type: 'text/csv;charset=utf-8;',
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'early_bird_rewards.csv');

      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download CSV:', error);
    }
  };

  return (
    <DashboardContent>
      <PermissionGuard resource="whitelist-early-bird" action="read" fallback={<ErrorPage403 />}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="medium">
          {translate('whitelist.earlyBird.title')}
        </Typography>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.earlyBird.total')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.totalQuota : 0} {translate('whitelist.personUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.earlyBird.claimed')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="success.main">
                  {summary ? summary.received : 0} {translate('whitelist.personUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.earlyBird.remaining')}
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {summary ? summary.remaining : 0} {translate('whitelist.personUnit')}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" variant="subtitle2">
                  {translate('whitelist.earlyBird.issued')}
                </Typography>
                <Typography variant="h4" fontWeight="bold" color="primary.main">
                  {summary ? summary.rewardsIssuedUsdt : 0} USDT
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {summary?.tiers && summary.tiers.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight="medium" sx={{ mb: 1.5 }}>
              {translate('whitelist.earlyBird.tierBreakdown')}
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontWeight: 600 } }}>
                    <TableCell align="right">{translate('whitelist.earlyBird.tierRewardAmount')}</TableCell>
                    <TableCell align="right">{translate('whitelist.earlyBird.tierCountTotal')}</TableCell>
                    <TableCell align="right">{translate('whitelist.earlyBird.tierCountPaid')}</TableCell>
                    <TableCell align="right">{translate('whitelist.earlyBird.tierCommitted')}</TableCell>
                    <TableCell align="right">{translate('whitelist.earlyBird.tierPaid')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.tiers.map((tier, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell align="right">{tier.amountUsdc} USDT</TableCell>
                      <TableCell align="right">{tier.countTotal}</TableCell>
                      <TableCell align="right">{tier.countPaid}</TableCell>
                      <TableCell align="right">{tier.subtotalCommittedUsdc} USDT</TableCell>
                      <TableCell align="right">{tier.subtotalPaidUsdc} USDT</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        <Paper
          sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}
        >
          <TextField
            placeholder={translate('whitelist.earlyBird.search')}
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250, flexGrow: 1 }}
          />
          <PermissionGuard resource="whitelist-early-bird" action="export" fallback={null}>
            <Button variant="outlined" startIcon={<GetAppIcon />} onClick={exportToCSV}>
              {translate('whitelist.exportCSV')}
            </Button>
          </PermissionGuard>
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small" sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: '#fff',
                  '& th': {
                    backgroundColor: '#fff',
                    fontWeight: 600,
                  },
                }}
              >
                <TableCell>{translate('whitelist.earlyBird.rank')}</TableCell>
                <TableCell>{translate('whitelist.earlyBird.address')}</TableCell>
                <TableCell>{translate('whitelist.earlyBird.email')}</TableCell>
                <TableCell align="right">{translate('whitelist.earlyBird.volume')}</TableCell>
                <TableCell align="right">{translate('whitelist.earlyBird.shouldIssue')}</TableCell>
                <TableCell align="center">{translate('whitelist.earlyBird.claimStatus')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow key={record.userId}>
                  <TableCell>{record.rank !== null ? `#${record.rank}` : '-'}</TableCell>
                  <TableCell>{record.proxyWalletAddress}</TableCell>
                  <TableCell>{record.email}</TableCell>
                  <TableCell align="right">
                    {Number(record.cumulativeVolumeUsdc).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </TableCell>
                  <TableCell align="right">{record.rewardUsdt} USDT</TableCell>
                  <TableCell align="center">
                    <Chip
                      label={
                        record.claimed
                          ? translate('whitelist.earlyBird.claimed')
                          : translate('whitelist.earlyBird.unclaim')
                      }
                      color={record.claimed ? 'success' : 'default'}
                      size="small"
                      variant={record.claimed ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 20, 50]}
          />
        </TableContainer>
      </Box>
      </PermissionGuard>
    </DashboardContent>
  );
};

export default View;
